// F1 App-Shell (TES-948): Badge-Ableitungen fuer die Haupt-Navigation.
// Aus AppLayout.tsx extrahiert — gleiche Counts (useOpenInspections/-Defects/-Tasks),
// gleiche Farb-/Pulse-Logik. Nutzbar von SideNav, NavDrawer und BottomNav.
// Map-basiert statt Ternary-Ketten (geringe Komplexitaet).
import type { ShellModel } from "./useShellModel";

export interface NavBadgeInfo {
  showBadge: boolean;
  badgeCount: number;
  badgeColor: string;
  shouldPulse: boolean;
  overdue: number;
}

// TES-969 (D7, Industrial-Mock): Count-Pill-Farben als DS-Token, module-scope
// (Muster toggle-tone.ts). Default = Safety-Yellow (--sidebar-primary) mit dunkler
// On-Yellow-Foreground (WCAG-AA); ueberfaellig bleibt ROT (destructive) — das
// Warnsignal ist funktional wichtiger als reine Mock-Treue. Gilt ueberall, wo
// NavBadgeDot/NavBadgePill rendern (SideNav expanded+collapsed, BottomNav, Drawer).
const BADGE_DEFAULT_COLOR = "bg-sidebar-primary text-sidebar-primary-foreground";
const BADGE_OVERDUE_COLOR = "bg-destructive text-destructive-foreground";

function badgeColorFor(overdue: number): string {
  return overdue > 0 ? BADGE_OVERDUE_COLOR : BADGE_DEFAULT_COLOR;
}

const EMPTY_BADGE: NavBadgeInfo = {
  showBadge: false,
  badgeCount: 0,
  badgeColor: BADGE_DEFAULT_COLOR,
  shouldPulse: false,
  overdue: 0,
};

// Pro Haupt-Route: woher Count/Overdue/Pulse kommen (Selektoren auf das ShellModel).
const BADGE_SELECTORS: Record<
  string,
  {
    count: (m: ShellModel) => number;
    overdue: (m: ShellModel) => number;
    pulse: (m: ShellModel) => boolean;
  }
> = {
  "/defects": {
    count: (m) => m.openDefects,
    overdue: (m) => m.overdueDefectsCount,
    pulse: (m) => m.isDefectsPulsing,
  },
  "/inspections": {
    count: (m) => m.openInspections,
    overdue: (m) => m.overdueInspectionsCount,
    pulse: (m) => m.isInspectionsPulsing,
  },
  "/tasks": {
    count: (m) => m.openTasks,
    overdue: (m) => m.overdueTasksCount,
    pulse: (m) => m.isTasksPulsing,
  },
};

export function getNavBadgeInfo(href: string, model: ShellModel): NavBadgeInfo {
  const sel = BADGE_SELECTORS[href];
  if (!sel) return EMPTY_BADGE;

  const badgeCount = sel.count(model);
  const overdue = sel.overdue(model);

  return {
    showBadge: badgeCount > 0,
    badgeCount,
    // Rot bei ueberfaellig, sonst Safety-Yellow (DS-Token, s. badgeColorFor)
    badgeColor: badgeColorFor(overdue),
    shouldPulse: sel.pulse(model),
    overdue,
  };
}
